import type { UserSourceHeaderName } from './config';
import { sanitizeTrackingValue } from './trackingValue';

export const CUSTOMER_SOURCE_HEADERS_KEY = 'customer_source_headers';
const ATTRIBUTION_TTL_DAYS = 30;
const ATTRIBUTION_TTL_MS = ATTRIBUTION_TTL_DAYS * 24 * 60 * 60 * 1000;

/**
 * Browser-local attribution cache.
 *
 * Parsed URL source params are stored as backend-ready `x-*` headers so later
 * registration/API requests can keep attribution even after navigation.
 */
type StoredCustomerSourceHeaders = {
  headers: Record<string, string>;
  expiresAt: number;
};

function isObject(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function sanitizeHeaders(headers: Record<string, unknown>): Record<string, string> {
  return Object.entries(headers).reduce<Record<string, string>>((acc, [name, value]) => {
    const normalizedValue = sanitizeTrackingValue(value);
    if (normalizedValue) {
      acc[name] = normalizedValue;
    }
    return acc;
  }, {});
}

export function readCustomerSourceHeadersFromStorage(): Record<string, string> {
  if (typeof window === 'undefined') {
    return {};
  }

  try {
    const raw = window.localStorage.getItem(CUSTOMER_SOURCE_HEADERS_KEY);
    if (!raw) {
      return {};
    }

    const parsed = JSON.parse(raw) as unknown;

    // Backward compatibility with previous format: plain header object.
    if (isObject(parsed) && !('headers' in parsed) && !('expiresAt' in parsed)) {
      return sanitizeHeaders(parsed);
    }

    if (!isObject(parsed) || !isObject(parsed.headers) || typeof parsed.expiresAt !== 'number') {
      window.localStorage.removeItem(CUSTOMER_SOURCE_HEADERS_KEY);
      return {};
    }

    if (Date.now() > parsed.expiresAt) {
      window.localStorage.removeItem(CUSTOMER_SOURCE_HEADERS_KEY);
      return {};
    }

    return sanitizeHeaders(parsed.headers);
  } catch {
    return {};
  }
}

/**
 * Merges newly parsed attribution headers into the existing 30-day cache.
 * New values win, missing values do not clear older attribution.
 */
export function writeCustomerSourceHeadersToStorage(
  headers: Record<string, string>,
): void {
  if (typeof window === 'undefined') {
    return;
  }

  const sanitizedIncoming = sanitizeHeaders(headers);
  if (!Object.keys(sanitizedIncoming).length) {
    return;
  }

  const existing = readCustomerSourceHeadersFromStorage();
  const merged = {
    ...existing,
    ...sanitizedIncoming,
  };

  const payload: StoredCustomerSourceHeaders = {
    headers: merged,
    expiresAt: Date.now() + ATTRIBUTION_TTL_MS,
  };

  window.localStorage.setItem(CUSTOMER_SOURCE_HEADERS_KEY, JSON.stringify(payload));
}

/**
 * Removes one attribution header from storage and keeps the rest intact.
 *
 * Used after successful registration to consume `x-affiliate-token` once while
 * preserving UTM/click attribution for the normal 30-day storage window.
 */
export function removeCustomerSourceHeaderFromStorage(headerName: UserSourceHeaderName): void {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    const raw = window.localStorage.getItem(CUSTOMER_SOURCE_HEADERS_KEY);
    if (!raw) {
      return;
    }

    const parsed = JSON.parse(raw) as unknown;

    // Backward compatibility with previous format: plain header object.
    if (isObject(parsed) && !('headers' in parsed) && !('expiresAt' in parsed)) {
      const headers = sanitizeHeaders(parsed);
      delete headers[headerName];

      if (!Object.keys(headers).length) {
        window.localStorage.removeItem(CUSTOMER_SOURCE_HEADERS_KEY);
        return;
      }

      const payload: StoredCustomerSourceHeaders = {
        headers,
        expiresAt: Date.now() + ATTRIBUTION_TTL_MS,
      };

      window.localStorage.setItem(CUSTOMER_SOURCE_HEADERS_KEY, JSON.stringify(payload));
      return;
    }

    if (!isObject(parsed) || !isObject(parsed.headers) || typeof parsed.expiresAt !== 'number') {
      window.localStorage.removeItem(CUSTOMER_SOURCE_HEADERS_KEY);
      return;
    }

    if (Date.now() > parsed.expiresAt) {
      window.localStorage.removeItem(CUSTOMER_SOURCE_HEADERS_KEY);
      return;
    }

    const headers = sanitizeHeaders(parsed.headers);
    delete headers[headerName];

    if (!Object.keys(headers).length) {
      window.localStorage.removeItem(CUSTOMER_SOURCE_HEADERS_KEY);
      return;
    }

    const payload: StoredCustomerSourceHeaders = {
      headers,
      expiresAt: parsed.expiresAt,
    };

    window.localStorage.setItem(CUSTOMER_SOURCE_HEADERS_KEY, JSON.stringify(payload));
  } catch {
    // Ignore storage errors; attribution cleanup should not block auth flows.
  }
}
